import { Auth0Provider } from "@auth0/auth0-react";
import AuthGuard from "@/components/AuthGuard/index.ts";
import FullPageSpinner from "@/components/FullPageSpinner/index.ts";
import MultiProvider from "@/components/MultiProvider/index.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { CssBaseline, createTheme } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { SnackbarProvider } from "notistack";
import { type ReactNode, StrictMode } from "react";
import { ConfirmProvider } from "react-confirm-hook";
import { createRoot } from "react-dom/client";
import {
    createQueryOptionsFactory,
    type QueryOptionsFactory,
    QueryOptionsFactoryProvider,
} from "@/queries";
import { routeTree } from "@/routeTree.gen.ts";
import { extendedReplaceEqualDeep } from "@/utils/api.ts";

const container = document.getElementById("root");

if (!container) {
    throw new Error("Root element missing");
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            structuralSharing: extendedReplaceEqualDeep,
            retry: false,
        },
    },
});

const root = createRoot(container);

const router = createRouter({
    routeTree,
    defaultPendingComponent: FullPageSpinner,
    context: {
        queryClient,
        // Injected through React
        qof: undefined as unknown as QueryOptionsFactory,
    },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    InnerWrap: ({ children }) => <AuthGuard>{children}</AuthGuard>,
});

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

const App = (): ReactNode => {
    const authFetch = useAuthenticatedFetch();
    const queryOptionsFactory = createQueryOptionsFactory(authFetch);

    return (
        <QueryOptionsFactoryProvider factory={queryOptionsFactory}>
            <RouterProvider
                router={router}
                context={{
                    qof: queryOptionsFactory,
                }}
            />
        </QueryOptionsFactoryProvider>
    );
};

const theme = createTheme({
    palette: {
        primary: {
            main: "#3b82f6",
            dark: "#1e40af",
            light: "#60a5fa",
        },
        secondary: {
            main: "#fbbf24",
            dark: "#f59e0b",
            light: "#fcd34d",
        },
        background: {
            default: "#f8fafc",
            paper: "#ffffff",
        },
        text: {
            primary: "#1e293b",
            secondary: "#64748b",
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h3: {
            fontSize: 24,
            color: "#386288",
        },
        h4: {
            fontWeight: 700,
        },
        h6: {
            fontWeight: 600,
        },
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    borderRadius: 8,
                },
            },
        },
    },
});

root.render(
    <StrictMode>
        <MultiProvider
            providerCreators={[
                (children) => (
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        {children}
                    </ThemeProvider>
                ),
                (children) => (
                    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
                ),
                (children) => (
                    <Auth0Provider
                        domain={import.meta.env.VITE_APP_AUTH0_DOMAIN}
                        clientId={import.meta.env.VITE_APP_AUTH0_CLIENT_ID}
                        authorizationParams={{
                            redirect_uri: window.location.origin,
                            audience: import.meta.env.VITE_APP_AUTH0_AUDIENCE,
                        }}
                        onRedirectCallback={(appState) => {
                            void router.navigate({ to: appState?.returnTo ?? "/" });
                        }}
                        useRefreshTokens
                        cacheLocation="localstorage"
                    >
                        {children}
                    </Auth0Provider>
                ),
                (children) => <SnackbarProvider>{children}</SnackbarProvider>,
                (children) => <ConfirmProvider>{children}</ConfirmProvider>,
            ]}
        >
            <App />
        </MultiProvider>
    </StrictMode>,
);
